import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TagIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '@apollo/client';
import ProfileIcon from '~/assets/icons/ProfileIcon';
import AttributeField from './AttributeField';
import { useGetCustomersByPersonId } from '~/services/customer';
import { useGetUserActivity, ActivityType } from '~/services/conversations';
import { GET_PROFILE_ATTRIBUTES } from '~/services/ProfileAttributes';
import { GET_PROFILE_VALUES, UPDATE_PROFILE_VALUE, CREATE_PROFILE_VALUE } from '~/services/ProfileValues';

// Extended ProfileAttribute interface to match backend response
interface ProfileAttribute {
  _id?: string;
  id?: string;
  name: string;
  dataType: 'text' | 'number' | 'boolean' | 'date' | 'closelist' | 'openlist';
  description?: string;
  useAI: boolean;
  closelist?: {
    options: string[];
    multiple: boolean;
  };
}

interface CustomAttribute {
  id: string;
  name: string;
  value: string;
  type: string;
  updated_at?: string;
}

interface Customer {
  _id: string;
  person_id: string;
  name: string;
  email?: string;
  phone?: string;
  created_at: string;
  updated_at?: string;
  last_activity?: string;
  custom_attributes?: CustomAttribute[];
}

interface UserActivity {
  _id: string;
  activity_type: ActivityType;
  created_at: string;
  updated_at?: string;
  ended_at?: string;
  messaging_type?: string;
  close_reason?: string;
  closed_by?: string;
  tags?: Array<{
    _id: string;
    name: string;
    description: string;
    color: string;
  }>;
}

const CustomerProfile = () => {
  const [t] = useTranslation('customer');
  const { customerId, agentId } = useParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [attributeValues, setAttributeValues] = useState<{[key: string]: any}>({});
  const [editingAttributes, setEditingAttributes] = useState<{[key: string]: boolean}>({});
  const [editedValues, setEditedValues] = useState<{[key: string]: any}>({});
  const [profileValueIds, setProfileValueIds] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState<{[key: string]: boolean}>({});
  
  const [getCustomer, { data, loading: queryLoading, error }] = useGetCustomersByPersonId();
  const [getUserActivity, { data: activityData, loading: activityQueryLoading, error: activityError }] = useGetUserActivity();

  // Get profile attributes
  const { data: attributesData, loading: attributesLoading } = useQuery(GET_PROFILE_ATTRIBUTES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId,
  });

  // Get profile values
  const { data: valuesData, loading: valuesLoading, refetch: refetchValues } = useQuery(GET_PROFILE_VALUES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId,
  });

  // Mutations for updating/creating profile values
  const [updateProfileValue] = useMutation(UPDATE_PROFILE_VALUE);
  const [createProfileValue] = useMutation(CREATE_PROFILE_VALUE);

  // Process profile values when available
  useEffect(() => {
    if (valuesData?.get_profile_values?.data && !valuesLoading && customerId) {
      const profileValues = valuesData.get_profile_values.data;
      const valuesByAttribute: {[key: string]: any} = {};
      const profileValueIds: {[key: string]: string} = {};
      
      // Group values by attribute_id and person_id
      profileValues.forEach((value: any) => {
        if (value.person_id === customerId) {
          const attributeKey = value.profile_attribute_id;
          valuesByAttribute[attributeKey] = value.value;
          profileValueIds[attributeKey] = value._id; // Store the profile value ID for updates
        }
      });
      
      setAttributeValues(valuesByAttribute);
      setProfileValueIds(profileValueIds);
      setEditedValues(valuesByAttribute); // Initialize edited values with current values
    }
  }, [valuesData, valuesLoading, customerId]);

  // Load customer data on component mount
  useEffect(() => {
    const loadCustomer = async () => {
      if (!customerId || !agentId) return;
      
      setLoading(true);
      try {
        await getCustomer({
          variables: {
            data: {
              agent_id: agentId,
              person_id: customerId,
            },
          },
        });
      } catch (error) {
        console.error('Error loading customer:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [customerId, agentId, getCustomer]);

  // Load user activities
  useEffect(() => {
    const loadActivities = async () => {
      if (!customerId || !agentId) return;
      
      setActivitiesLoading(true);
      try {
        await getUserActivity({
          variables: {
            data: {
              agent_id: agentId,
              user_id: customerId,
            },
          },
        });
      } catch (error) {
        console.error('Error loading activities:', error);
      } finally {
        setActivitiesLoading(false);
      }
    };

    loadActivities();
  }, [customerId, agentId, getUserActivity]);

  // Handle GraphQL response
  useEffect(() => {
    if (data?.get_customers_by_person_id) {
      const response = data.get_customers_by_person_id;
      if (!response.hasError && response.data?.customer) {
        const customerData = response.data.customer;
        
        const formattedCustomer: Customer = {
          _id: customerData.person_id,
          person_id: customerData.person_id,
          name: customerData.profile_name || `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim() || 'Unknown',
          email: customerData.personChannels?.find((ch: any) => ch.channel_type === 'email')?.channel_id || '',
          phone: customerData.personChannels?.find((ch: any) => ch.channel_type === 'whatsapp')?.channel_id || '',
          created_at: customerData.created_at,
          updated_at: customerData.updated_at,
          last_activity: customerData.updated_at,
          custom_attributes: (customerData.profileValues || []).map((pv: any) => {
            let formattedValue: string;
            let valueType: string;

            // Determine value type and format accordingly
            if (Array.isArray(pv.value)) {
              formattedValue = pv.value.join(', ');
              valueType = 'array';
            } else if (typeof pv.value === 'boolean') {
              formattedValue = pv.value ? t('yes', 'Sí') : t('no', 'No');
              valueType = 'boolean';
            } else if (typeof pv.value === 'number') {
              formattedValue = pv.value.toString();
              valueType = 'number';
            } else {
              formattedValue = pv.value || '';
              valueType = 'string';
            }

            return {
              id: pv._id,
              name: pv.profile_attribute_name,
              value: formattedValue,
              type: valueType,
              updated_at: pv.updated_at,
            };
          }),
        };
        
        setCustomer(formattedCustomer);
      } else {
        console.error('Error in response:', response.message);
        setCustomer(null);
      }
    }
  }, [data]);

  // Handle user activities response
  useEffect(() => {
    if (activityData?.response) {
      const response = activityData.response;
      if (!response.hasError && response.data) {
        const activitiesData = response.data.map((activity: any) => ({
          _id: activity._id,
          activity_type: activity.activity_type,
          created_at: activity.created_at,
          updated_at: activity.updated_at,
          ended_at: activity.ended_at,
          messaging_type: activity.messaging_type,
          close_reason: activity.close_reason,
          closed_by: activity.closed_by,
          tags: activity.tags || [],
        }));
        setActivities(activitiesData);
      } else {
        console.error('Error in activities response:', response.message);
        setActivities([]);
      }
    }
  }, [activityData]);

  // Find customer by ID
  // const customer = mockCustomers.find(c => c._id === customerId);

  // Get activity color based on type
  const getActivityColor = (activityType: ActivityType) => {
    switch (activityType) {
      case ActivityType.CONVERSATIONS:
        return 'bg-primary';
      case ActivityType.COMMENTS:
        return 'bg-secondary';
      case ActivityType.CAMPAIGNS:
        return 'bg-accent';
      case ActivityType.FILE:
        return 'bg-warning';
      default:
        return 'bg-base-300';
    }
  };

  // Get activity type label
  const getActivityTypeLabel = (activityType: ActivityType) => {
    switch (activityType) {
      case ActivityType.CONVERSATIONS:
        return t('activity_conversations', 'Conversations');
      case ActivityType.COMMENTS:
        return t('activity_comments', 'Comments');
      case ActivityType.CAMPAIGNS:
        return t('activity_campaigns', 'Campaigns');
      case ActivityType.FILE:
        return t('activity_file', 'File');
      default:
        return activityType;
    }
  };

  // Handle activity click navigation
  const handleActivityClick = (activity: UserActivity) => {
    if (activity.activity_type === ActivityType.CONVERSATIONS && agentId && customerId) {
      const conversationId = activity._id;
      const hasOpenConversation = !activity.ended_at; // If no ended_at, conversation is still open
      const url = `/agent/${agentId}/conversations/${customerId}?conversationId=${conversationId}&hasOpenConversation=${hasOpenConversation}`;
      window.open(url, '_blank');
    }
  };

  // Generate activity description based on activity data
  const getActivityDescription = (activity: UserActivity) => {
    switch (activity.activity_type) {
      case ActivityType.CONVERSATIONS:
        let description = t('conversation_activity', 'Conversation');
        if (activity.messaging_type) {
          description += ` (${activity.messaging_type})`;
        }
        if (activity.ended_at) {
          description = t('conversation_ended', 'Conversation ended');
          if (activity.close_reason === 'SYSTEM_CLOSED') {
            description += ` - ${t('system_closed', 'System closed')}`;
          }
        }
        
        return description;
      case ActivityType.COMMENTS:
        return t('comment_activity', 'Comment added');
      case ActivityType.CAMPAIGNS:
        return t('campaign_activity', 'Campaign activity');
      case ActivityType.FILE:
        return t('file_activity', 'File activity');
      default:
        return activity.activity_type;
    }
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate relative time
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      return t('days_ago', '{{count}} days ago', { count: diffInDays });
    } else if (diffInHours > 0) {
      return t('hours_ago', '{{count}} hours ago', { count: diffInHours });
    } else {
      return t('recently', 'Recently');
    }
  };

  // Format attribute value for display
  const formatAttributeValue = (attribute: CustomAttribute) => {
    if (!attribute.value || attribute.value === '' || attribute.value === 'null' || attribute.value === 'undefined') {
      return t('no_value', 'Sin valor');
    }

    switch (attribute.type) {
      case 'boolean':
        // The value should already be formatted as "Sí"/"No" from the processing above
        return attribute.value;
      case 'array':
        return attribute.value;
      case 'number':
        return attribute.value;
      default:
        return attribute.value;
    }
  };

  // Handle edit mode toggle
  const handleEditClick = (attributeId: string) => {
    setEditingAttributes(prev => ({
      ...prev,
      [attributeId]: true
    }));
    // Initialize edited value with current value
    setEditedValues(prev => ({
      ...prev,
      [attributeId]: attributeValues[attributeId] || ''
    }));
  };

  // Handle cancel edit
  const handleCancelEdit = (attributeId: string) => {
    setEditingAttributes(prev => ({
      ...prev,
      [attributeId]: false
    }));
    // Reset edited value to original
    setEditedValues(prev => ({
      ...prev,
      [attributeId]: attributeValues[attributeId] || ''
    }));
  };

  // Handle value change during edit
  const handleValueChange = (attributeId: string, value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [attributeId]: value
    }));
  };

  // Handle save attribute
  const handleSaveAttribute = async (attributeId: string) => {
    if (!customerId || !agentId) return;

    setSaving(prev => ({ ...prev, [attributeId]: true }));

    try {
      const newValue = editedValues[attributeId];
      const existingProfileValueId = profileValueIds[attributeId];

      if (existingProfileValueId) {
        // Update existing profile value
        await updateProfileValue({
          variables: {
            data: {
              profile_value_id: existingProfileValueId,
              agent_id: agentId,
              value: newValue
            }
          }
        });
      } else {
        // Create new profile value
        await createProfileValue({
          variables: {
            data: {
              agent_id: agentId,
              profile_id: customerId, // person_id is used as profile_id
              attribute_id: attributeId,
              value: newValue
            }
          }
        });
      }

      // Update local state
      setAttributeValues(prev => ({
        ...prev,
        [attributeId]: newValue
      }));

      // Exit edit mode
      setEditingAttributes(prev => ({
        ...prev,
        [attributeId]: false
      }));

      // Refetch values to get updated data
      refetchValues();

    } catch (error) {
      console.error('Error saving attribute:', error);
      // Could add a toast notification here
    } finally {
      setSaving(prev => ({ ...prev, [attributeId]: false }));
    }
  };

  if (loading || queryLoading || attributesLoading || valuesLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="text-base-content/70 mt-4">{t('loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-error">{t('error_loading_customer', 'Error loading customer')}</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-base-content/70">{t('customer_not_found', 'Customer not found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-center gap-4">
        <ProfileIcon isActive={true} />
        <div>
          <h3 className="text-xl font-semibold">{t('customer_profile', 'Customer Profile')}</h3>
          <p className="text-base-content/70">{t('profile_description', 'Personal information and details')}</p>
        </div>
      </div>

      {/* Profile Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Basic and Account Information */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="card bg-base-100 border-base-300 border shadow-sm">
            <div className="card-body">
              <h4 className="card-title text-lg">{t('basic_info', 'Información de la Cuenta')}</h4>
              <div className="space-y-3">
                
                <div>
                  <label className="text-sm font-medium text-base-content/70">{t('name', 'Name')}</label>
                  <p className="text-base-content">{customer.name}</p>
                </div>
                {customer.email && (
                  <div>
                    <label className="text-sm font-medium text-base-content/70">{t('email', 'Email')}</label>
                    <p className="text-base-content">{customer.email}</p>
                  </div>
                )}
                {customer.phone && (
                  <div>
                    <label className="text-sm font-medium text-base-content/70">{t('phone', 'Phone')}</label>
                    <p className="text-base-content">{customer.phone}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-base-content/70">{t('created_at', 'Creado el')}</label>
                  <p className="text-base-content">{formatDate(customer.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-base-content/70">{t('last_activity', 'Última Actividad')}</label>
                  <p className="text-base-content">
                    {customer.last_activity ? getRelativeTime(customer.last_activity) : t('no_activity', 'No activity')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Custom Attributes */}
          {attributesData?.get_profile_attributes?.data && attributesData.get_profile_attributes.data.length > 0 && (
            <div className="card bg-base-100 border-base-300 border shadow-sm">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="card-title text-lg flex items-center gap-2">
                    <TagIcon className="h-5 w-5 text-primary" />
                    {t('recent_attributes', 'Características del usuario')}
                  </h4>
                  <Link 
                    to={`/agent/${agentId}/customers/${customerId}/attributes`}
                    className="btn btn-ghost btn-sm text-primary hover:bg-primary hover:text-primary-content"
                  >
                    {t('view_attributes', 'Ver características')}
                  </Link>
                 </div>
                <div className="space-y-3">
                  {attributesData.get_profile_attributes.data
                    .filter((attr: ProfileAttribute) => {
                      const attributeId = attr.id || attr._id || '';
                      return attributeValues[attributeId] !== undefined && attributeValues[attributeId] !== null && attributeValues[attributeId] !== '';
                    })
                    .slice(0, 5)
                    .map((attribute: ProfileAttribute) => {
                      const attributeId = attribute.id || attribute._id || '';

                      return (
                        <AttributeField
                          key={attributeId}
                          attribute={attribute}
                          attributeId={attributeId}
                          value={editingAttributes[attributeId] ? editedValues[attributeId] : attributeValues[attributeId]}
                          isEditing={editingAttributes[attributeId]}
                          isSaving={saving[attributeId]}
                          onEdit={handleEditClick}
                          onCancel={handleCancelEdit}
                          onSave={handleSaveAttribute}
                          onChange={handleValueChange}
                          displayMode="inline"
                          showAIIndicator={false}
                          showTypeIndicator={false}
                        />
                      );
                    })}
                </div>
                {attributesData.get_profile_attributes.data.filter((attr: ProfileAttribute) => {
                  const attributeId = attr.id || attr._id || '';
                  return attributeValues[attributeId] !== undefined && attributeValues[attributeId] !== null && attributeValues[attributeId] !== '';
                }).length > 5 && (
                  <div className="flex justify-between items-center mt-4">
                    <p className="text-xs text-base-content/50">
                      {t('more_attributes', 'y {{count}} atributos más...', { 
                        count: attributesData.get_profile_attributes.data.filter((attr: ProfileAttribute) => {
                          const attributeId = attr.id || attr._id || '';
                          return attributeValues[attributeId] !== undefined && attributeValues[attributeId] !== null && attributeValues[attributeId] !== '';
                        }).length - 5 
                      })}
                    </p>
                    <Link 
                      to={`/agent/${agentId}/customers/${customerId}/attributes`}
                      className="btn btn-primary btn-sm"
                    >
                      {t('view_more', 'Ver más')}
                    </Link>
                  </div>
                )}
                
                {/* Description for recent attributes (file-only Spanish copy) */}
                <div className="mt-4 pt-3 border-t border-base-300">
                  <p className="text-xs text-base-content/60 italic">
                    {t('recent_attributes_description', 'Aquí se muestran las últimas 5 actualizaciones realizadas en el usuario.')}
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Right Column - Activities */}
        <div className="lg:row-span-2 mb-5">
          <div className="card bg-base-100 border-base-300 border shadow-sm h-full">
            <div className="card-body">
              <h4 className="card-title text-lg">{t('activities', 'Activities')}</h4>
              {activitiesLoading || activityQueryLoading ? (
                <div className="flex justify-center py-8">
                  <div className="loading loading-spinner loading-md"></div>
                </div>
              ) : activityError ? (
                <div className="text-center py-8">
                  <p className="text-error text-sm">{t('error_loading_activities', 'Error loading activities')}</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-base-content/70 text-sm">{t('no_activities', 'No activities found')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activities
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, 10)
                    .map((activity) => (
                      <div 
                        key={activity._id} 
                        className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                          activity.activity_type === ActivityType.CONVERSATIONS 
                            ? 'hover:bg-base-200 cursor-pointer' 
                            : ''
                        }`}
                        onClick={() => handleActivityClick(activity)}
                      >
                        <div className={`w-2 h-2 ${getActivityColor(activity.activity_type)} rounded-full mt-2`}></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium">{getActivityDescription(activity)}</p>
                            <span className="badge badge-outline badge-xs">
                              {getActivityTypeLabel(activity.activity_type)}
                            </span>
                          </div>
                          <p className="text-xs text-base-content/70">{getRelativeTime(activity.created_at)}</p>
                          {activity.tags && activity.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {activity.tags.slice(0, 2).map((tag) => (
                                <span 
                                  key={tag._id} 
                                  className="badge badge-xs"
                                  style={{ backgroundColor: tag.color, color: '#000' }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                              {activity.tags.length > 2 && (
                                <span className="badge badge-ghost badge-xs">
                                  +{activity.tags.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  {activities.length > 10 && (
                    <div className="text-center pt-4">
                      <p className="text-xs text-base-content/50">
                        {t('more_activities', 'y {{count}} actividades más...', { count: activities.length - 10 })}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
